"use client";

import React from "react";
import type { Deal } from "@/lib/types/database/deals";
import { DealDetailMetricsPanel } from "../DealDetailMetricsPanel";
import { DealDetailActionsBar } from "../DealDetailActionsBar";
import { InlineTagsEditor } from "@/components/shared/UniversalDetailPage/components/InlineTagsEditor";
import { CollapsibleSection } from "@/components/shared/CollapsibleSection";
import { AISummary } from "@/components/ai/ai-summary";
import { DEAL_TIMELINE_SUMMARY_OPTIONS } from "@/lib/domain/crm-entity-relationships";
import { hasDealStage, isWonDeal, isLostStage } from "@/lib/constants/deal-stages";
import { unwrapArray } from "@/lib/utils/crm/graphql-types";
import { ENHANCED_DEALS_CONFIG } from "../EnhancedDealsConfig";
import { FieldRow } from "@/components/shared/InlineEditableInfoPanel/components/FieldRow";
import { useFieldEditing } from "@/components/shared/InlineEditableInfoPanel/hooks/useFieldEditing";
import { useFieldFiltering } from "@/lib/hooks/search/useFieldFiltering";
import { DEAL_DETAIL_SECTION_FIELD_IDS } from "./deal-detail-tab-fields";

interface DealDetailsTabProps {
  item: Deal;
  onTagsUpdate?: (newTags: string[]) => Promise<void>;
  /**
   * Save handler for inline field edits. When provided (WTC employees/admins),
   * the DB-backed fields below become click-to-edit in place. Absent for client
   * users, so the tab renders read-only.
   */
  onInlineEdit?: (deal: Deal) => Promise<void>;
}

export function DealDetailsTab({ item, onTagsUpdate, onInlineEdit }: DealDetailsTabProps) {
  const { roleName } = useFieldFiltering();
  const {
    editingField,
    editedItem,
    handleFieldEdit,
    handleSaveField,
    handleCancelEdit,
    handleFieldValueChange,
  } = useFieldEditing<Deal>(item, onInlineEdit);

  // Deals are editable by WTC employees/admins only; client users never receive
  // an onInlineEdit handler (suppressed upstream), so this doubly guards edits.
  const canEdit = Boolean(onInlineEdit) && roleName !== "client_user";

  const renderField = (fieldId: string): React.ReactNode => {
    const fieldConfig = ENHANCED_DEALS_CONFIG.fields[fieldId];
    if (!fieldConfig) return null;
    return (
      <FieldRow
        key={fieldId}
        fieldId={fieldId}
        fieldConfig={fieldConfig}
        value={editedItem[fieldId as keyof Deal]}
        isFieldEditing={editingField === fieldId}
        canEdit={canEdit}
        isFullscreen={false}
        item={item}
        config={ENHANCED_DEALS_CONFIG}
        onFieldEdit={handleFieldEdit}
        onSaveField={handleSaveField}
        onCancelEdit={handleCancelEdit}
        onFieldValueChange={handleFieldValueChange}
      />
    );
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
        {/* Status glance — where the deal stands, first thing a rep sees. */}
        <div className="lg:col-span-1 order-2 lg:order-1">
          <DealDetailMetricsPanel deal={item} />
        </div>

        {/* Reading column — get up to speed: title, notes, then everything else
            behind progressive disclosure. Field editing now primarily lives in
            the right-hand inline properties panel; the "Deal Details" section
            below stays as a secondary in-tab editor (and the sole editor on the
            full page, which has no side panel). */}
        <div className="lg:col-span-2 space-y-4 md:space-y-6 order-1 lg:order-2">
          <div>
            <h2 className="text-xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">{item.title}</h2>
          </div>

          <div className="border-t pt-4">{renderField("notes")}</div>

          {/* AI deal summary — on-demand and collapsed by default so it never
              buries the notes/status a rep opens the deal to read. */}
          <CollapsibleSection title="AI summary" defaultCollapsed>
            <AISummary
              entityType="deal"
              entityId={item.id}
              summaryType="timeline"
              options={{ ...DEAL_TIMELINE_SUMMARY_OPTIONS }}
              showMetadata={true}
              showRefreshButton={true}
            />
          </CollapsibleSection>

          <div className="border-t pt-4">{renderField("description")}</div>

          <div className="border-t pt-4">{renderField("company_id")}</div>

          <CollapsibleSection title="Deal Details" defaultCollapsed>
            <div className="space-y-4">
              {DEAL_DETAIL_SECTION_FIELD_IDS.map((fieldId) => renderField(fieldId))}
            </div>
          </CollapsibleSection>

          {(item.tags && item.tags.length > 0) || onTagsUpdate ? (
            <div className="border-t pt-4">
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 block">
                Tags
              </label>
              {onTagsUpdate ? (
                <InlineTagsEditor
                  tags={unwrapArray(item.tags)}
                  entityId={item.id}
                  entityType="deals"
                  onTagsUpdate={onTagsUpdate}
                  maxTags={20}
                  maxDisplayed={10}
                />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {item.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/40 px-3 py-1 text-sm font-medium text-blue-800 dark:text-blue-200"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          <CollapsibleSection title="Timeline" defaultCollapsed>
            <div className="flex flex-col gap-2 md:grid md:grid-cols-3 md:gap-4 text-sm">
              <div className="flex items-baseline justify-between gap-2 md:block">
                <span className="text-xs text-gray-600 dark:text-gray-400 font-medium shrink-0">Created</span>
                <p className="text-gray-900 dark:text-gray-100 text-right md:text-left md:mt-1">
                  {new Date(item.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-baseline justify-between gap-2 md:block">
                <span className="text-xs text-gray-600 dark:text-gray-400 font-medium shrink-0">Updated</span>
                <p className="text-gray-900 dark:text-gray-100 text-right md:text-left md:mt-1">
                  {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : "—"}
                </p>
              </div>
              {hasDealStage(item.stage) && isWonDeal(item.stage) && item.won_at && (
                <div className="flex items-baseline justify-between gap-2 md:block">
                  <span className="text-xs text-gray-600 dark:text-gray-400 font-medium shrink-0">Won</span>
                  <p className="text-gray-900 dark:text-gray-100 text-right md:text-left md:mt-1">
                    {new Date(item.won_at).toLocaleDateString()}
                  </p>
                </div>
              )}
              {hasDealStage(item.stage) && isLostStage(item.stage) && item.lost_at && (
                <div className="flex items-baseline justify-between gap-2 md:block">
                  <span className="text-xs text-gray-600 dark:text-gray-400 font-medium shrink-0">Lost</span>
                  <p className="text-gray-900 dark:text-gray-100 text-right md:text-left md:mt-1">
                    {new Date(item.lost_at).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </CollapsibleSection>
        </div>
      </div>

      <div className="border-t pt-6">
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 block">
          Quick Actions
        </label>
        <DealDetailActionsBar deal={item} />
      </div>
    </div>
  );
}
